import * as v from 'valibot'
import { NovelPlayLanguage } from './play-config.js'

const Json = v.record(v.string(), v.unknown())
const Id = v.pipe(v.string(), v.minLength(1), v.maxLength(80))
const Count = v.pipe(v.number(), v.integer(), v.minValue(0))

/** Creator-only metadata. Never include prompt, response, provider detail or request body. */
export const NovelPlaytestCall = v.strictObject({
  task: v.nullable(v.string()),
  mode: v.nullable(v.string()),
  model: v.nullable(v.string()),
  status: v.nullable(v.string()),
  elapsed_ms: v.nullable(Count),
  error_code: v.nullable(v.string()),
})

export const NovelPlaytestDiagnostics = v.strictObject({
  play_language: v.nullable(v.string()),
  source_revision: v.nullable(Count),
  content_hash: v.nullable(v.string()),
  play_config_hash: v.nullable(v.string()),
  last_turn_seq: v.nullable(Count),
  request_status: v.nullable(v.string()),
  error_code: v.nullable(v.string()),
  last_turn_calls: v.array(NovelPlaytestCall),
})

/** White-list projection of persisted snapshot audits. Missing historical fields stay unknown. */
export function novelPlaytestDiagnostics(run) {
  const branch = run.branches.find(item => item.branch_id === run.active_branch_id)
  const last_turn = branch?.turns.at(-1)
  const request = Object.values(run.requests ?? {})
    .filter(item => item.branch_id === run.active_branch_id)
    .sort((left, right) => String(left.started_at ?? '').localeCompare(String(right.started_at ?? ''))).at(-1)
  const details = run.model_call_details ?? {}
  const callView = call => ({
    task: call.task ?? details[call.call_id]?.task ?? null,
    mode: call.mode ?? details[call.call_id]?.mode ?? null,
    model: call.model ?? details[call.call_id]?.model ?? null,
    status: call.status ?? null,
    elapsed_ms: call.elapsed_ms ?? null,
    error_code: call.error_code ?? null,
  })
  const request_calls = (request?.audits ?? []).flatMap(call => {
    const attempts = call.failed_attempts ?? []
    if (!attempts.length) return [callView(call)]
    return attempts.map(attempt => callView({ task: call.task, call_id: attempt.request_id,
      mode: call.mode, model: attempt.model, status: 'failed', elapsed_ms: attempt.elapsed_ms, error_code: call.error_code }))
  })
  // Background calls belong to the latest committed turn, never an older turn after a failed attempt.
  const jobs = last_turn && (!request || request.request_id === last_turn.request_id)
    ? (branch.jobs ?? []).filter(job => job.through_seq === last_turn.seq)
    : []
  return {
    play_language: run.play_language ?? null,
    source_revision: run.play_context?.source_revision ?? null,
    content_hash: run.package_hash ?? null,
    play_config_hash: run.play_context?.play_config_hash ?? null,
    last_turn_seq: last_turn?.seq ?? null,
    request_status: request?.status ?? null,
    error_code: request?.error_code ?? null,
    last_turn_calls: [...request_calls, ...jobs.flatMap(job => (job.calls ?? []).map(callView))],
  }
}

/**
 * 开局：只收已上架作品，作品当前内容整份冻进对局快照（形态 7.2、D22）；多余的键一律 400。
 * @typedef {{ pack_id: string }} NovelRunCreateBodyT
 */
export const NovelRunCreateBody = v.strictObject({
  pack_id: Id,
  play_language: v.optional(NovelPlayLanguage, 'en'),
})

/** 「我的对局」一行（书架"继续阅读"同用）；title 是作品当前标题，cover_url 是作品封面地址，与作品列表同源（C17）。 */
export const NovelRunSummary = v.object({
  run_id: v.string(),
  pack_id: v.string(),
  title: v.string(),
  play_language: v.string(),
  purpose: v.picklist(['play', 'playtest', 'comparison']),
  source_origin: v.string(),
  cover_url: v.nullable(v.string()),
  status: v.picklist(['active', 'archived']),
  turn_count: Count,
  has_pending_jobs: v.boolean(),
  last_played_at: v.string(),
  created_at: v.string(),
})

export const NovelRunList = v.object({ items: v.array(NovelRunSummary) })

/** 活动分支回合分页（按 seq 升序），段落只含玩家可见的。 */
export const NovelTurnPage = v.object({
  items: v.array(v.object({
    turn_id: v.string(),
    seq: Count,
    input_mode: v.nullable(v.string()),
    recording: v.string(),
    segments: v.array(Json),
  })),
  next_before_seq: v.nullable(Count),
})

/** 场外偏好；kind、value、scope 的取值由引擎 validate_preference 按作品校验。 */
export const NovelPreferenceBody = v.strictObject({
  branch_id: Id,
  kind: v.string(),
  value: v.unknown(),
  scope: v.string(),
})

/** DELETE /api/novel/runs/:id：对局与它的回合、调用归属行一并物理删除（形态 6.1，C9）。 */
export const NovelRunDeleteResponse = v.object({
  run_id: v.string(),
  deleted: v.literal(true),
})

/** 归档与取消归档的结果；取消归档只接受 archived 的对局，否则 CONFLICT。 */
export const NovelRunStatusChange = v.object({
  run_id: v.string(),
  status: v.picklist(['active', 'archived']),
})

export const NovelCandidateBody = v.strictObject({
  action: v.picklist(['adopt', 'discard']),
  branch_id: Id,
})

/**
 * 玩家资料：Demo `materials` 的 player 视图——世界设定、在场人物卡、当前地点、在场物品、已知事实、关系、
 * 回忆册（memories）与玩家可见的原文段落（sources）、未完成事项。内层是引擎结构，整体透传。
 */
export const NovelMaterials = v.object({
  scope: v.literal('player'),
  world_rules: v.string(),
  characters: v.array(Json),
  location: v.nullable(Json),
  items: v.array(Json),
  knowledge: v.array(Json),
  memories: v.array(Json),
  sources: v.array(Json),
  relations: v.array(Json),
  story_threads: v.array(Json),
})

/** 纠错的结果：刷新后的玩家资料，另带本次按 novel_correction 扣的积分。 */
export const NovelCorrectionResult = v.object({
  ...NovelMaterials.entries,
  charged_points: Count,
})

/**
 * 回忆册纠错（Demo `correct_memory`）：record_id 是回忆册里的一条记录，evidence_ids 是选作依据的原文段落，
 * 更正说明上限 800 字。记录、依据与说明是否成立由引擎校验（invalid_correction / invalid_evidence / invalid_text）；
 * 每次提交按 novel_correction 预扣积分，核对不通过或失败时退还。
 * @typedef {{ record_id: string, text: string, evidence_ids: string[] }} NovelCorrectionBodyT
 */
export const NovelCorrectionBody = v.strictObject({
  record_id: Id,
  text: v.pipe(v.string(), v.maxLength(800)),
  evidence_ids: v.array(v.pipe(v.string(), v.minLength(1), v.maxLength(200))),
})
